import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, Download } from "lucide-react";
import { LOGO_URL, BRAND } from "@/config";
import { SaleCelebration } from "@/components/SaleCelebration";
import { TrustBadges } from "@/components/TrustBadges";

const getParam = (name) => new URLSearchParams(window.location.search).get(name);

export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking, success, failed, timeout
  const [track, setTrack] = useState(null);

  useEffect(() => {
    const sessionId = getParam("session_id");
    if (!sessionId) { setState("failed"); return; }
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 6) { setState("timeout"); return; }
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setTrack(data.track);
          setState("success");
          return;
        }
        if (data.status === "expired") { setState("failed"); return; }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, []);

  return (
    <div className="min-h-screen grain flex items-center justify-center px-6 py-10">
      <div className="relative z-10 bg-[#121212] border border-[#2a2a2a] rounded-2xl p-10 max-w-md w-full text-center">
        <div className="flex items-center justify-center gap-2 mb-8">
          <img src={LOGO_URL} alt={BRAND} className="h-8 w-8 object-contain" />
          <span className="font-heading font-extrabold text-lg tracking-tight">{BRAND}</span>
        </div>

        {state === "checking" && (
          <div data-testid="checkout-checking">
            <Loader2 className="w-14 h-14 animate-spin text-[#e2ff31] mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-semibold">Confirming your payment…</h1>
            <p className="text-[#9ca3af] mt-2">Hang tight, this only takes a moment.</p>
          </div>
        )}

        {state === "success" && (
          <div data-testid="checkout-success">
            <CheckCircle2 className="w-16 h-16 text-[#22c55e] mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-semibold">Payment successful!</h1>
            <p className="text-[#9ca3af] mt-2">
              {track ? <>You now own <span className="text-[#e2ff31]">{track.title}</span>.</> : "Your beat is ready."}
            </p>
            <button data-testid="goto-library-btn" onClick={() => navigate("/purchases")}
              className="mt-6 w-full flex items-center justify-center gap-2 rounded-full bg-[#e2ff31] text-black hover:bg-[#c8e619] font-semibold py-3 transition-colors">
              <Download className="w-4 h-4" /> Go to My Purchases
            </button>
            {track && <SaleCelebration track={track} />}
            <TrustBadges compact className="mt-6 pt-5 border-t border-[#2a2a2a]" />
            <p className="text-[11px] text-[#6b7280] mt-3">
              All sales are final. File problem? See our <Link to="/refund-policy" data-testid="success-refund-policy-link" className="text-[#e2ff31] underline">Refund Policy</Link>.
            </p>
          </div>
        )}

        {(state === "failed" || state === "timeout") && (
          <div data-testid="checkout-failed">
            <XCircle className="w-16 h-16 text-[#ef4444] mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-semibold">
              {state === "timeout" ? "Still processing" : "Payment not completed"}
            </h1>
            <p className="text-[#9ca3af] mt-2">
              {state === "timeout"
                ? "We couldn't confirm yet. Check your library shortly."
                : "Your payment was cancelled or failed."}
            </p>
            <button onClick={() => navigate("/")}
              className="mt-6 w-full rounded-full border border-[#2a2a2a] hover:bg-[#1e1e1e] font-semibold py-3 transition-colors">
              Back to catalog
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
